import { Stack, Text } from '@mantine/core'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useLocation, useNavigate } from 'react-router'
import { ModelNestedPath, buildModelDetailsLink } from '@/router/paths'
import { MLModel, MLModelStatus } from '@/types/model'
import { ModelStep, ModelStepper } from './ModelStepper/ModelStepper'

const modelSteps: ModelStep[] = [
  {
    id: 1,
    messageId: 'models.stepper.step1',
    urlPath: ModelNestedPath.selectBaseModel
  },
  {
    id: 2,
    messageId: 'models.stepper.step2',
    urlPath: ModelNestedPath.selectDataset
  },
  {
    id: 3,
    messageId: 'models.stepper.step3',
    urlPath: ModelNestedPath.labelData
  },
  {
    id: 4,
    messageId: 'models.stepper.step4',
    urlPath: ModelNestedPath.training
  }
]

type ModelStepperHandlerProps = {
  appId: string
  model: MLModel
}

export const ModelStepperHandler = ({
  appId,
  model
}: ModelStepperHandlerProps) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [disabledSteps, setDisabledSteps] = useState<number[]>([])

  const activeStepIndex = modelSteps.findIndex((step) =>
    pathname.includes(step.urlPath)
  )

  const handleStepClick = (step: ModelStep) => {
    void navigate(buildModelDetailsLink(appId, model.id, step.urlPath))
  }

  useEffect(() => {
    if (model.status === MLModelStatus.Trained) {
      setCompletedSteps([0, 1, 2, 3])
      setDisabledSteps([0, 1, 2])
    } else if (model.training !== null) {
      setCompletedSteps([0, 1, 2])
      setDisabledSteps([0, 1, 2])
    } else if (
      model.status === MLModelStatus.PreTrained &&
      model.dataset_version !== null
    ) {
      setCompletedSteps([0, 1])
      setDisabledSteps([3])
    } else if (model.status === MLModelStatus.PreTrained) {
      setCompletedSteps([0])
      setDisabledSteps([2, 3])
    } else if (model.status === MLModelStatus.Draft) {
      setCompletedSteps([])
      setDisabledSteps([1, 2, 3])
    } else {
      setCompletedSteps([])
      setDisabledSteps([])
    }
  }, [model.status, model.training, model.dataset_version])

  return (
    <Stack gap="xs">
      <Text size="xs" ml="xs" fw={700} tt="uppercase">
        <FormattedMessage
          id="models.stepper.title"
          values={{
            completed: completedSteps.length,
            total: modelSteps.length
          }}
        />
      </Text>

      <ModelStepper
        steps={modelSteps}
        activeStep={activeStepIndex}
        completedSteps={completedSteps}
        disabledSteps={disabledSteps}
        onStepClick={handleStepClick}
      />
    </Stack>
  )
}
